import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function PinIcon() {
    return (
        <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
        >
            <path d="M12 21s-7-6.5-7-12a7 7 0 1 1 14 0c0 5.5-7 12-7 12z" />
            <circle cx="12" cy="9" r="2.5" />
        </svg>
    );
}
function SearchIcon() {
    return (
        <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
        >
            <circle cx="11" cy="11" r="7" />
            <path d="m21 21-4.3-4.3" />
        </svg>
    );
}
function LoaderRing() {
    return (
        <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            className="animate-spin"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
        >
            <path d="M21 12a9 9 0 1 1-6.219-8.56" />
        </svg>
    );
}

function formatLocation(loc) {
    if (!loc) return "";
    const parts = [loc.city, loc.state, loc.country].filter(Boolean);
    return parts.join(", ");
}

export default function CourseFinder({ onApply }) {
    const [query, setQuery] = useState("");
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [place, setPlace] = useState(null);
    const [reason, setReason] = useState(null);
    const [locating, setLocating] = useState(false);
    const abortRef = useRef(null);

    const runSearch = useCallback(async (q) => {
        if (!q || !q.trim()) return;
        setLoading(true);
        try {
            if (abortRef.current) abortRef.current.abort();
            abortRef.current = new AbortController();
            const r = await axios.get(`${API}/courses/search`, {
                params: { q },
                signal: abortRef.current.signal,
                timeout: 35000,
            });
            setResults(r.data.courses || []);
            setPlace(r.data.place || null);
            setReason(r.data.reason || null);
            if ((r.data.courses || []).length === 0 && r.data.reason !== "rate_limited") {
                toast(
                    r.data.place
                        ? `Found ${r.data.place.city || "place"} but no golf courses tagged there. Try a nearby city.`
                        : "No place matched. Try a different spelling.",
                );
            }
        } catch (e) {
            if (e.name !== "CanceledError") {
                setReason("error");
                toast.error(
                    "Search timed out. OpenStreetMap is busy — try again in a moment.",
                );
            }
        } finally {
            setLoading(false);
        }
    }, [abortRef]);

    useEffect(() => {
        const q = query.trim();
        if (!q) {
            setResults([]);
            setPlace(null);
            setReason(null);
            return;
        }
        const t = setTimeout(() => runSearch(q), 600);
        return () => clearTimeout(t);
    }, [query, runSearch]);

    const useMyLocation = async () => {
        if (!navigator.geolocation) {
            toast.error("Location isn't supported on this device.");
            return;
        }
        setLocating(true);
        navigator.geolocation.getCurrentPosition(
            async (pos) => {
                try {
                    const { latitude, longitude } = pos.coords;
                    const r = await axios.get(`${API}/courses/nearby`, {
                        params: {
                            lat: latitude,
                            lng: longitude,
                            radius_km: 40,
                        },
                    });
                    setResults(r.data.courses || []);
                    setPlace(r.data.place || null);
                    if (r.data.place?.city) setQuery(r.data.place.city);
                    if ((r.data.courses || []).length === 0) {
                        toast("No courses found within 40km. Try widening.");
                    } else {
                        toast.success(
                            `Found ${r.data.courses.length} courses near ${
                                r.data.place?.city || "you"
                            }`,
                        );
                    }
                } catch {
                    toast.error("Nearby search failed.");
                } finally {
                    setLocating(false);
                }
            },
            () => {
                setLocating(false);
                toast.error("Location permission denied.");
            },
            { enableHighAccuracy: false, timeout: 8000 },
        );
    };

    const pick = (c) => {
        const label = c.club + (c.course ? ` — ${c.course}` : "");
        const parts = String(c.id || "").split("/");
        
        let osmType, osmId;
        if (c.provider === 'google') {
            osmType = 'google';
            osmId = c.id;
        } else {
            osmType = parts[0];
            osmId = parts[1] || "";
        }
        
        onApply({
            courseLabel: label,
            location: c.location,
            osmType,
            osmId,
            club: c.club,
            city: c.location?.city || null,
            country: c.location?.country || null,
        });
        setResults([]);
        setQuery("");
    };

    return (
        <div>
            {/* Search bar */}
            <div className="flex flex-col sm:flex-row gap-2 mb-3">
                <div className="flex-1 relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--smoke)]">
                        <SearchIcon />
                    </div>
                    <input
                        data-testid="course-search-input"
                        type="text"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="City, course name, or region — e.g. Pinehurst, St Andrews, Chicago"
                        className="w-full bg-[#0f1113] hairline-strong h-11 pl-9 pr-9 text-[color:var(--bone)] font-body focus:outline-none focus:border-[color:var(--volt)]"
                        style={{ borderRadius: 3 }}
                    />
                    {loading && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2 text-[color:var(--volt)]">
                            <LoaderRing />
                        </div>
                    )}
                </div>
                <button
                    onClick={useMyLocation}
                    disabled={locating}
                    data-testid="use-my-location-button"
                    className="btn-ghost inline-flex items-center gap-2 justify-center"
                >
                    {locating ? <LoaderRing /> : <PinIcon />}
                    Near me
                </button>
            </div>

            {loading && (
                <div
                    className="eyebrow mb-2 text-[color:var(--smoke)]"
                    data-testid="search-loading-hint"
                >
                    Searching OpenStreetMap · this can take a few seconds
                </div>
            )}

            {reason === "rate_limited" && !loading && (
                <div
                    className="mb-3 p-3 hairline surface flex items-center justify-between"
                    style={{
                        borderRadius: 3,
                        borderLeft: "3px solid var(--danger)",
                    }}
                    data-testid="search-rate-limited"
                >
                    <div>
                        <div className="eyebrow" style={{ color: "var(--danger)" }}>
                            OpenStreetMap is busy
                        </div>
                        <div className="text-xs text-[color:var(--smoke)] mt-1">
                            Its public mirrors are rate-limited right now.
                            Try again in a few seconds.
                        </div>
                    </div>
                    <button
                        onClick={() => runSearch(query.trim())}
                        data-testid="search-retry-button"
                        className="text-xs uppercase tracking-[0.2em] text-[color:var(--volt)] hover:text-[color:var(--bone)]"
                    >
                        Retry
                    </button>
                </div>
            )}

            {place && (
                <div
                    className="eyebrow mb-2 text-[color:var(--smoke)]"
                    data-testid="location-context"
                >
                    Around{" "}
                    <span className="text-[color:var(--bone)]">
                        {[place.city, place.state, place.country]
                            .filter(Boolean)
                            .join(", ")}
                    </span>
                </div>
            )}

            {/* Result list */}
            <AnimatePresence initial={false}>
                {results.length > 0 && (
                    <motion.div
                        initial={{ opacity: 0, y: -4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.15 }}
                        className="max-h-80 overflow-y-auto surface hairline"
                        style={{ borderRadius: 3 }}
                        data-testid="course-search-results"
                    >
                        {results.map((c, i) => (
                            <button
                                key={`${c.provider}-${c.id}-${i}`}
                                onClick={() => pick(c)}
                                data-testid={`course-result-${i}`}
                                className="w-full text-left px-4 py-3 border-b hairline hover:bg-[color:var(--surface-2)] transition-colors"
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div className="flex-1 min-w-0">
                                        <div className="font-display font-[700] text-sm sm:text-base uppercase tracking-wide truncate">
                                            {c.club}
                                            {c.course ? (
                                                <span className="text-[color:var(--smoke)] normal-case font-normal">
                                                    {" "}
                                                    · {c.course}
                                                </span>
                                            ) : null}
                                        </div>
                                        <div className="text-xs text-[color:var(--smoke)] mt-0.5 truncate">
                                            {formatLocation(c.location) ||
                                                c.location?.address ||
                                                "Location unknown"}
                                        </div>
                                    </div>
                                    <div className="text-right shrink-0">
                                        {c.holes ? (
                                            <div className="chip">
                                                <span className="text-[color:var(--bone)]">
                                                    {c.holes}h
                                                </span>
                                            </div>
                                        ) : null}
                                        <div className="text-[10px] text-[color:var(--smoke)] mt-1 tracking-[0.16em] uppercase">
                                            {c.provider === 'google' ? 'GOOGLE' : 'OSM'}
                                        </div>
                                    </div>
                                </div>
                            </button>
                        ))}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
